import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Bookmark, Trash2, ArrowRight, FileSpreadsheet, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function MappingProfiles() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProfiles = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/mapping-profiles`);
      setProfiles(Array.isArray(r.data) ? r.data : []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load mapping profiles');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchProfiles(); }, []);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/mapping-profiles/${id}`);
      toast.success('Profile deleted');
      await fetchProfiles();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to delete profile');
    }
  };

  return (
    <div data-testid="mapping-profiles-page" className="space-y-6 max-w-4xl">
      <div className="cp-page-header">
        <h1 className="cp-page-title flex items-center gap-2">
          <Bookmark className="w-6 h-6 text-accent" /> Mapping Profiles
        </h1>
        <Link to="/settings/import-data">
          <Button variant="outline" size="sm" data-testid="go-to-import-btn">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Go to Import
          </Button>
        </Link>
      </div>

      <Card className="p-5 bg-muted/50 animate-fade-in" data-testid="profiles-info">
        <p className="text-xs text-muted-foreground leading-relaxed">
          Mapping profiles save your column mappings and unit conversions for reuse.
          When you import a CSV with the same source system and data type, the saved profile
          will be suggested automatically. Create profiles from the
          <Link to="/settings/import-data" className="text-accent hover:underline mx-1">Import CSV</Link>
          page during the mapping step.
        </p>
      </Card>

      <div className="cp-table-wrapper animate-fade-in" data-testid="profiles-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Profile Name</TableHead>
              <TableHead>Source System</TableHead>
              <TableHead>Data Type</TableHead>
              <TableHead>Mapped Fields</TableHead>
              <TableHead>Unit Conversions</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              [...Array(2)].map((_, i) => (
                <TableRow key={`sk-${i}`}>{[...Array(7)].map((__, j) => <TableCell key={`sk-${i}-${j}`}><div className="h-4 bg-muted rounded animate-pulse" /></TableCell>)}</TableRow>
              ))
            ) : profiles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                  <Bookmark className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No mapping profiles saved yet.</p>
                  <p className="text-xs mt-1">Create one during CSV import by clicking &quot;Save Profile&quot;.</p>
                </TableCell>
              </TableRow>
            ) : profiles.map(p => {
              const mappedCount = Object.keys(p.column_mapping || {}).filter(k => p.column_mapping[k] !== '_skip_').length;
              const totalFields = Object.keys(p.column_mapping || {}).length;
              const unitConvs = Object.entries(p.unit_conversions || {}).filter(([, v]) => v && v !== 'kWh');
              return (
                <TableRow key={p.id} data-testid={`profile-row-${p.id}`}>
                  <TableCell className="font-medium">{p.name}</TableCell>
                  <TableCell><span className="cp-badge-indigo text-xs">{p.source_system}</span></TableCell>
                  <TableCell className="text-sm">{p.data_type}</TableCell>
                  <TableCell>
                    <span className="text-xs text-muted-foreground">{mappedCount}/{totalFields} fields</span>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {Object.entries(p.column_mapping || {}).filter(([, v]) => v !== '_skip_').map(([target, source]) => (
                        <span key={target} className="inline-flex items-center gap-0.5 text-[10px] bg-muted rounded px-1.5 py-0.5">
                          {source} <ArrowRight className="w-2 h-2" /> {target}
                        </span>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    {unitConvs.length > 0 ? (
                      <div className="flex flex-col gap-0.5">
                        {unitConvs.map(([field, unit]) => (
                          <span key={field} className="inline-flex items-center gap-0.5 text-[10px] text-amber-600">
                            <Zap className="w-2.5 h-2.5" /> {field}: {unit} <ArrowRight className="w-2 h-2" /> kWh
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">None</span>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {p.created_at ? new Date(p.created_at).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(p.id)}
                      className="text-destructive hover:text-destructive" data-testid={`delete-profile-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
